/**
 * Vstupy bodů A a B: textové hledání s našeptávačem (online/offline),
 * aktuální poloha, výběr v mapě, ruční souřadnice, pojmenování, prohození.
 */
import { geocode, reverseGeocode, type GeocodeHit } from '../data/geocode';
import type { NamedPoint } from '../core/types';
import { activeParams, type Store } from '../state/store';
import { el, clear, debounce } from './dom';
import { icon } from './icons';

export interface PointInputsCallbacks {
  onPickOnMap(which: 'origin' | 'destination'): void;
}

export function createPointInputs(store: Store, cb: PointInputsCallbacks): {
  root: HTMLElement;
  refresh(): void;
} {
  const root = el('section', { className: 'card', 'aria-label': 'Odkud a kam' });

  const title = el('h2', { className: 'card__title' }, 'Odkud a kam');

  const rowA = buildRow('origin');
  const swap = el(
    'div',
    { className: 'swap-row' },
    el(
      'button',
      {
        className: 'btn btn--ghost btn--small',
        title: 'Prohodit A a B',
        'aria-label': 'Prohodit body A a B',
        onclick: () => {
          const s = store.get();
          store.update({ origin: s.destination, destination: s.origin, selectedItineraryId: null });
        },
      },
      '⇅ Prohodit',
    ),
  );
  const rowB = buildRow('destination');

  root.append(title, rowA.root, swap, rowB.root);

  function buildRow(which: 'origin' | 'destination'): { root: HTMLElement; refresh(): void } {
    const isA = which === 'origin';
    const listboxId = `search-listbox-${isA ? 'a' : 'b'}`;
    const input = el('input', {
      className: 'input',
      type: 'text',
      placeholder: isA
        ? 'Start: adresa, místo, nebo „50.08, 14.43“'
        : 'Cíl: adresa, místo, nebo souřadnice',
      'aria-label': isA ? 'Bod A — start' : 'Bod B — cíl',
      autocomplete: 'off',
      role: 'combobox',
      'aria-expanded': 'false',
      'aria-controls': listboxId,
      'aria-autocomplete': 'list',
    }) as HTMLInputElement;

    const resultsBox = el('div', {
      className: 'search-results',
      id: listboxId,
      role: 'listbox',
      'aria-label': isA ? 'Návrhy pro bod A' : 'Návrhy pro bod B',
      style: 'display:none',
    });

    const nameInput = el('input', {
      className: 'input',
      type: 'text',
      placeholder: 'Pojmenovat (např. Domov)',
      'aria-label': isA ? 'Název bodu A' : 'Název bodu B',
      style: 'margin-top:6px;display:none',
      onchange: (e: Event) => {
        const v = (e.target as HTMLInputElement).value.trim();
        const s = store.get();
        const p = s[which];
        if (p) store.update({ [which]: { ...p, name: v || undefined } } as never);
      },
    }) as HTMLInputElement;

    const coordsLabel = el('div', { className: 'muted', style: 'margin-top:4px;display:none' });

    let hits: GeocodeHit[] = [];
    let activeIdx = -1;
    let closed = false;
    /** Zneplatnění opožděných výsledků hledání (Esc/výběr nesmí být „přebit" sítí). */
    let searchSeq = 0;

    const renderHits = (notice?: string): void => {
      clear(resultsBox);
      if (closed || (hits.length === 0 && !notice)) {
        resultsBox.style.display = 'none';
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        return;
      }
      resultsBox.style.display = '';
      input.setAttribute('aria-expanded', 'true');
      if (activeIdx >= 0 && activeIdx < hits.length) {
        input.setAttribute('aria-activedescendant', `${listboxId}-opt-${activeIdx}`);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
      if (notice) resultsBox.append(el('div', { className: 'search-notice', role: 'status' }, notice));
      hits.forEach((h, i) => {
        resultsBox.append(
          el(
            'button',
            {
              className: 'search-result',
              id: `${listboxId}-opt-${i}`,
              role: 'option',
              type: 'button',
              'aria-selected': String(i === activeIdx),
              onclick: () => choose(h),
            },
            el('div', { className: 'search-result__name' }, sourceIcon(h.source) + ' ' + h.name),
            el('div', { className: 'search-result__detail' }, h.detail),
          ),
        );
      });
    };

    const choose = (h: GeocodeHit): void => {
      searchSeq++;
      closed = true;
      hits = [];
      renderHits();
      input.value = h.name;
      const address =
        h.source === 'coords' ? h.name : h.detail ? `${h.name}, ${h.detail}` : h.name;
      const point: NamedPoint = { lat: h.lat, lng: h.lng, address, name: undefined };
      store.update({ [which]: point, selectedItineraryId: null } as never);
    };

    const doSearch = debounce(async (q: string) => {
      const myId = ++searchSeq;
      const s = store.get();
      const params = activeParams(s);
      const res = await geocode(q, {
        online: params.get('geocode.online') && navigator.onLine !== false,
        endpoint: params.get('geocode.nominatimEndpoint') || undefined,
      });
      // Mezitím uživatel zavřel našeptávač, vybral, nebo změnil dotaz.
      if (myId !== searchSeq) return;
      // Geografická věrohodnost: výsledky seřadíme podle blízkosti k druhému bodu.
      const other = isA ? s.destination : s.origin;
      hits = res.hits;
      if (other && hits.length > 1) {
        hits = [...hits].sort(
          (x, y) =>
            Math.hypot(x.lat - other.lat, x.lng - other.lng) -
            Math.hypot(y.lat - other.lat, y.lng - other.lng),
        );
      }
      activeIdx = hits.length > 0 ? 0 : -1;
      closed = false;
      renderHits(res.notice);
    }, 320);

    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (q.length < 2) {
        searchSeq++;
        hits = [];
        closed = true;
        renderHits();
        return;
      }
      doSearch(q);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIdx = Math.min(activeIdx + 1, hits.length - 1);
        renderHits();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIdx = Math.max(activeIdx - 1, 0);
        renderHits();
      } else if (e.key === 'Enter' && activeIdx >= 0 && hits[activeIdx]) {
        e.preventDefault();
        choose(hits[activeIdx]);
      } else if (e.key === 'Escape') {
        searchSeq++;
        closed = true;
        renderHits();
      }
    });

    document.addEventListener('click', (e) => {
      if (!resultsBox.contains(e.target as Node) && e.target !== input) {
        searchSeq++;
        closed = true;
        renderHits();
      }
    });

    const geoBtn = isA
      ? el(
          'button',
          {
            className: 'btn btn--small btn--icon',
            title: 'Použít aktuální polohu',
            'aria-label': 'Použít aktuální polohu jako bod A',
            onclick: () => locate(),
          },
          icon('locate', { size: 15 }),
        )
      : null;

    const mapBtn = el(
      'button',
      {
        className: 'btn btn--small btn--icon',
        title: 'Vybrat kliknutím do mapy',
        'aria-label': `Vybrat ${isA ? 'bod A' : 'bod B'} v mapě`,
        onclick: () => cb.onPickOnMap(which),
      },
      icon('map-pin', { size: 15 }),
    );

    const clearBtn = el(
      'button',
      {
        className: 'btn btn--small btn--icon btn--ghost',
        title: 'Smazat bod',
        'aria-label': `Smazat ${isA ? 'bod A' : 'bod B'}`,
        onclick: () => {
          input.value = '';
          store.update({ [which]: null, selectedItineraryId: null } as never);
        },
      },
      icon('close', { size: 14 }),
    );

    function locate(): void {
      if (!('geolocation' in navigator)) {
        showNotice('Prohlížeč nepodporuje zjištění polohy. Zadejte adresu, nebo klikněte do mapy.');
        return;
      }
      input.value = 'Zjišťuji polohu…';
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const p: NamedPoint = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            name: 'Moje poloha',
          };
          store.update({ origin: p, selectedItineraryId: null });
          const params = activeParams(store.get());
          const addr = await reverseGeocode(p, {
            online: params.get('geocode.online'),
            endpoint: params.get('geocode.nominatimEndpoint') || undefined,
          });
          if (addr) {
            const cur = store.get().origin;
            if (cur && cur.name === 'Moje poloha') store.update({ origin: { ...cur, address: addr } });
          }
        },
        (err) => {
          input.value = '';
          showNotice(
            err.code === err.PERMISSION_DENIED
              ? 'Přístup k poloze byl odepřen. Zadejte start ručně, nebo klikněte do mapy.'
              : 'Polohu se nepodařilo zjistit. Zadejte start ručně, nebo klikněte do mapy.',
          );
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
      );
    }

    function showNotice(text: string): void {
      hits = [];
      closed = false;
      renderHits(text);
      window.setTimeout(() => {
        closed = true;
        renderHits();
      }, 6000);
    }

    const rowEl = el(
      'div',
      { className: 'point-row' },
      el('div', { className: `point-dot point-dot--${isA ? 'a' : 'b'}`, 'aria-hidden': 'true' }, isA ? 'A' : 'B'),
      el('div', { className: 'search-wrap grow' }, input, resultsBox, nameInput, coordsLabel),
      el('div', { className: 'point-actions' }, ...(geoBtn ? [geoBtn] : []), mapBtn, clearBtn),
    );

    const refresh = (): void => {
      const p = store.get()[which];
      if (p) {
        const display = p.name || p.address || `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;
        if (document.activeElement !== input) input.value = display;
        nameInput.style.display = '';
        if (document.activeElement !== nameInput) nameInput.value = p.name ?? '';
        coordsLabel.style.display = '';
        coordsLabel.textContent = `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}${p.address && p.name ? ` · ${p.address}` : ''}`;
      } else {
        nameInput.style.display = 'none';
        coordsLabel.style.display = 'none';
        if (document.activeElement !== input) input.value = '';
      }
    };

    return { root: rowEl, refresh };
  }

  return {
    root,
    refresh: () => {
      rowA.refresh();
      rowB.refresh();
    },
  };
}

function sourceIcon(source: GeocodeHit['source']): string {
  if (source === 'coords') return '🎯';
  if (source === 'offline') return '💾';
  return '🔎';
}
